// How the Drawings organizer's saves update the stored sheet list. Pure; see tests/reduxStore/pageListUpdates.test.ts.

import type { drawingPage } from './types'

export type OrganizerResult<T> =
  | { ok: true; data: T }
  | { ok: false; status?: number; message: string; featureName?: string }

/** Only the organizer's fields change: the canvas may hold unsaved takeoff the response doesn't know about. */
export function applyPageMetadata(pages: drawingPage[], saved: drawingPage[]): drawingPage[] {
  const byId = new Map(saved.filter((p) => p.id).map((p) => [p.id, p]))
  return pages.map((page) => {
    const fresh = page.id ? byId.get(page.id) : undefined
    if (!fresh) return page
    return {
      ...page,
      pageType: fresh.pageType,
      pdfPageNumber: fresh.pdfPageNumber,
      starred: fresh.starred,
      folderId: fresh.folderId,
    }
  })
}

/** Carried-forward sheets join the list; one already listed is replaced. */
export function addPages(pages: drawingPage[], added: drawingPage[]): drawingPage[] {
  const addedById = new Map(added.filter((p) => p.id).map((p) => [p.id, p]))
  const kept = pages.map((page) => (page.id && addedById.has(page.id) ? (addedById.get(page.id) as drawingPage) : page))
  const listed = new Set(pages.map((p) => p.id))
  return [...kept, ...added.filter((p) => !listed.has(p.id))]
}

/** A failed request as the organizer shows it: the server's reason when it gave one, and the Pro feature it gates. */
export function toFailure(
  error: unknown,
  fallback: string,
): { ok: false; status?: number; message: string; featureName?: string } {
  const response = (
    error as { response?: { status?: number; data?: { message?: string; detail?: string; featureName?: string } } }
  )?.response
  const reason = response?.data?.message || response?.data?.detail
  const failure = { ok: false as const, status: response?.status, message: reason || fallback }
  return response?.status === 402 && response.data?.featureName
    ? { ...failure, featureName: response.data.featureName }
    : failure
}
